import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import { distinctUntilChanged, map as rxMap, Subscription } from 'rxjs';
import { Point2D } from '@shared/models/geometry.model';
import { getTooltipBaseStyles } from '@client/style/tooltip.styles';

import { STOREY_MIN_HEIGHT } from '@shared/models/design/storey.model';
import type { DesignMap } from '../map';
import { useSetSelectedStoreyId } from '../store/design-store.hooks';
import {
  MapTooltipCornerAngleRef,
  MapTooltipPayload,
  MapTooltipSegmentRef,
} from '../models/map-observables.models';
import { BLUEPRINT_CALIBRATE_COLOR } from '../blueprint-underlay/blueprint-underlay-controller';
import { Tooltip } from '@ui/Tooltip';
import { Icon } from '@ui/Icon';
import {
  formatSegmentLengthMeters,
  normalizeSegmentLengthInput,
  parseSegmentLengthInput,
} from '../helpers/segment-length-label.helpers';
import {
  formatCornerAngleDegrees,
  parseCornerAngleInput,
} from '../helpers/corner-angle-edit.helpers';
import { fitSegmentLabelScreenPoint } from '../helpers/selected-segment.helpers';

type MapTooltipLayerProps = {
  map?: DesignMap;
  disabled?: boolean;
  /** Extra labels (e.g. calibrate measure) merged with interaction tooltips. */
  extraTooltips?: MapTooltipPayload[];
};

const useStyles = makeStyles()((theme) => ({
  layer: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 2,
  },
  tooltip: {
    ...getTooltipBaseStyles(theme),
    position: 'absolute',
    whiteSpace: 'nowrap',
  },
  tooltipDisabled: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.grey[700],
  },
  selectedMeasurement: {
    '& input:not(:focus)': { color: 'rgba(0, 0, 0, 0.8)' },
    '& [data-storey-height-icon]': { color: 'rgba(0, 0, 0, 0.8)' },
  },
  tooltipMuted: {
    opacity: 0.9,
  },
  tooltipCalibrate: {
    border: `1px solid ${BLUEPRINT_CALIBRATE_COLOR}`,
    fontVariantNumeric: 'tabular-nums',
  },
  tooltipSegmentLength: {
    padding: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'rgba(0, 0, 0, 0.5)',
    fontSize: 13,
    fontWeight: 400,
    letterSpacing: 0,
    pointerEvents: 'auto',
    cursor: 'pointer',
    '&[data-point-dragging]': {
      pointerEvents: 'none',
    },
    '&:not([data-point-dragging]):hover': {
      '& input:not(:focus)': {
        backgroundColor: 'rgba(204, 204, 204, 0.7)',
        color: 'rgba(0, 0, 0, 0.8)',
      },
      '& span:not([data-segment-length-sizer])': {
        visibility: 'visible',
        color: 'rgba(0, 0, 0, 0.8)',
      },
    },
    '&:focus-within': {
      '& span:not([data-segment-length-sizer])': {
        visibility: 'visible',
        color: 'rgba(0, 0, 0, 0.8)',
      },
    },
  },
  segmentLengthInput: {
    minWidth: '2ch',
    margin: 0,
    padding: '0 2px',
    border: '1px solid transparent',
    borderRadius: 2,
    outline: 0,
    background: 'transparent',
    color: 'rgba(0, 0, 0, 0.5)',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 500,
    lineHeight: '16px',
    textAlign: 'center',
    boxSizing: 'content-box',
    fontVariantNumeric: 'tabular-nums',
    appearance: 'textfield',
    fieldSizing: 'content',
    cursor: 'pointer',
    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      margin: 0,
      appearance: 'none',
    },
    '&:focus': {
      cursor: 'text',
      border: `1px solid ${theme.palette.custom.focus}`,
      backgroundColor: '#ffffff',
      color: 'rgba(0, 0, 0, 0.8)',
    },
  },
  cornerAngleInput: {
    paddingRight: 'calc(1ch + 2px)',
    textAlign: 'right',
  },
  storeyHeightIcon: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginRight: 2,
  },
  segmentLengthUnit: {
    visibility: 'hidden',
    fontWeight: 400,
  },
  segmentLengthSizer: {
    position: 'absolute',
    display: 'inline-block',
    visibility: 'hidden',
    padding: '0 2px',
    border: '1px solid transparent',
    fontWeight: 500,
    lineHeight: '16px',
    boxSizing: 'content-box',
    fontVariantNumeric: 'tabular-nums',
    pointerEvents: 'none',
    whiteSpace: 'pre',
  },
  cornerAngleSymbol: {
    position: 'absolute',
    top: '50%',
    right: 2,
    transform: 'translateY(-50%)',
    visibility: 'visible',
    pointerEvents: 'none',
    color: 'rgba(0, 0, 0, 0.5)',
    fontWeight: 500,
  },
  validationTooltipContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    whiteSpace: 'nowrap',
  },
  validationTooltip: {
    width: 'max-content',
    maxWidth: 'none',
  },
}));

const TOOLTIP_OFFSET_X = 16;
const TOOLTIP_OFFSET_Y = 14;

const point = (x: number, y: number): Point2D => [x, y];

/**
 * Stable key for a segment length label.
 */
const getSegmentKey = (
  segment: Pick<MapTooltipSegmentRef, 'shapeId' | 'startIndex' | 'endIndex'>,
): string =>
  `segment:${segment.shapeId}:${segment.startIndex}:${segment.endIndex}`;

const getCornerAngleKey = (corner: MapTooltipCornerAngleRef): string =>
  `corner:${corner.shapeId}:${corner.pointIndex}`;

const getMeasurementKey = (tooltip: MapTooltipPayload): string | undefined =>
  tooltip.segment
    ? getSegmentKey(tooltip.segment)
    : tooltip.cornerAngle
      ? getCornerAngleKey(tooltip.cornerAngle)
      : tooltip.storeyHeight
        ? `storey-height:${tooltip.storeyHeight.storeyId}`
        : undefined;

/** Resolve the adjacent rendered segment in visual clockwise/counterclockwise order. */
const getAdjacentMeasurementKey = (
  tooltips: readonly MapTooltipPayload[],
  currentKey: string,
  shapeId: string,
  pointsAreClockwise: boolean,
  reverse: boolean,
): string | undefined => {
  const polygonMeasurements = tooltips.filter(
    (tooltip) =>
      tooltip.segment?.shapeId === shapeId ||
      tooltip.cornerAngle?.shapeId === shapeId,
  );
  if (polygonMeasurements.length < 2) return undefined;
  const currentIndex = polygonMeasurements.findIndex(
    (candidate) => getMeasurementKey(candidate) === currentKey,
  );
  if (currentIndex < 0) return undefined;

  const clockwiseStep = pointsAreClockwise ? 1 : -1;
  const step = reverse ? -clockwiseStep : clockwiseStep;
  const adjacentIndex =
    (currentIndex + step + polygonMeasurements.length) %
    polygonMeasurements.length;
  const adjacent = polygonMeasurements[adjacentIndex];
  return adjacent ? getMeasurementKey(adjacent) : undefined;
};

/**
 * Resolve tooltip placement around an anchor.
 * Cursor tips stay inside the map; segment / calibrate lengths follow world space
 * (no edge clamp — otherwise they “stick” to the viewport while the line pans away).
 */
const getTooltipPosition = (input: {
  screenPoint: Point2D;
  tooltipSize: { width: number; height: number };
  containerSize: { width: number; height: number };
  /** Center the label on the anchor (used for segment / calibrate lengths). */
  centered?: boolean;
  /** Point inside the tooltip that should coincide with the screen anchor. */
  centeredOffset?: Point2D;
  /** When false, allow the tip to leave the map with its geometry. */
  clampToContainer?: boolean;
}): Point2D => {
  const [x, y] = input.screenPoint;
  const { width: tooltipWidth, height: tooltipHeight } = input.tooltipSize;
  const { width: containerWidth, height: containerHeight } =
    input.containerSize;

  const preferredLeft = input.centered
    ? x - (input.centeredOffset?.[0] ?? tooltipWidth / 2)
    : x + TOOLTIP_OFFSET_X + tooltipWidth <= containerWidth
      ? x + TOOLTIP_OFFSET_X
      : x - TOOLTIP_OFFSET_X - tooltipWidth;
  const preferredTop = input.centered
    ? y - (input.centeredOffset?.[1] ?? tooltipHeight / 2)
    : y + TOOLTIP_OFFSET_Y + tooltipHeight <= containerHeight
      ? y + TOOLTIP_OFFSET_Y
      : y - TOOLTIP_OFFSET_Y - tooltipHeight;

  if (input.clampToContainer === false) {
    return point(preferredLeft, preferredTop);
  }

  const clampedLeft = Math.max(
    0,
    Math.min(preferredLeft, containerWidth - tooltipWidth),
  );
  const clampedTop = Math.max(
    0,
    Math.min(preferredTop, containerHeight - tooltipHeight),
  );
  return point(clampedLeft, clampedTop);
};

/**
 * Place each tooltip from the latest screen projection.
 * Segment labels use the rendered input box so the control clearance stays stable.
 */
const measureMapTooltipPositions = ({
  tooltips,
  layerElement,
  tooltipElements,
}: {
  tooltips: readonly MapTooltipPayload[];
  layerElement: HTMLElement | null;
  tooltipElements: readonly (HTMLDivElement | null)[];
}): Point2D[] => {
  if (!tooltips.length) return EMPTY_POSITIONS;
  const layerBounds = layerElement?.getBoundingClientRect();
  return tooltips.map((tooltip, index) => {
    const centered =
      tooltip.anchor === 'center' ||
      tooltip.anchor === 'right' ||
      tooltip.tone === 'muted' ||
      tooltip.tone === 'calibrate';
    const unmeasured = centered
      ? point(tooltip.screenPoint[0], tooltip.screenPoint[1])
      : point(
          tooltip.screenPoint[0] + TOOLTIP_OFFSET_X,
          tooltip.screenPoint[1] + TOOLTIP_OFFSET_Y,
        );
    const tooltipElement = tooltipElements[index];
    if (!layerBounds || !tooltipElement) return unmeasured;

    const tooltipBounds = tooltipElement.getBoundingClientRect();
    const tooltipWidth = tooltipBounds.width;
    const tooltipHeight = tooltipBounds.height;
    if (!tooltipWidth || !tooltipHeight) return unmeasured;

    const inputBounds = tooltip.segment
      ? tooltipElement.querySelector('input')?.getBoundingClientRect()
      : undefined;
    const stableSizerBounds = tooltip.segment
      ? tooltipElement
          .querySelector<HTMLElement>('[data-segment-length-sizer]')
          ?.getBoundingClientRect()
      : undefined;
    const stableLabelBoxSize =
      inputBounds?.width && inputBounds.height
        ? {
            width: Math.max(inputBounds.width, stableSizerBounds?.width ?? 0),
            height: Math.max(
              inputBounds.height,
              stableSizerBounds?.height ?? 0,
            ),
          }
        : undefined;
    const screenPoint =
      tooltip.segment && stableLabelBoxSize
        ? fitSegmentLabelScreenPoint({
            start: tooltip.segment.screenStart,
            end: tooltip.segment.screenEnd,
            preferredPoint: tooltip.screenPoint,
            labelBoxSize: stableLabelBoxSize,
          })
        : tooltip.screenPoint;
    const centeredOffset = inputBounds
      ? point(
          inputBounds.left - tooltipBounds.left + inputBounds.width / 2,
          inputBounds.top - tooltipBounds.top + inputBounds.height / 2,
        )
      : undefined;
    return getTooltipPosition({
      screenPoint,
      tooltipSize: {
        width: tooltipWidth,
        height: tooltipHeight,
      },
      containerSize: {
        width: layerBounds.width,
        height: layerBounds.height,
      },
      centered,
      centeredOffset:
        tooltip.anchor === 'right'
          ? point(tooltipWidth, tooltipHeight / 2)
          : centeredOffset,
      // Length labels stay glued to the span — never dock to the viewport edge.
      clampToContainer:
        tooltip.tone !== 'calibrate' &&
        tooltip.anchor !== 'center' &&
        tooltip.anchor !== 'right' &&
        !tooltip.segment,
    });
  });
};

/**
 * Write tooltip positions in the same turn as the map camera event.
 * A React render of those positions can wait until zooming stops.
 */
const writeTooltipPositions = (
  tooltipElements: readonly (HTMLDivElement | null)[],
  positions: readonly Point2D[],
): void => {
  positions.forEach((position, index) => {
    const element = tooltipElements[index];
    if (!element) return;
    element.style.left = `${position[0]}px`;
    element.style.top = `${position[1]}px`;
  });
};

const EMPTY_EXTRA_TOOLTIPS: MapTooltipPayload[] = [];
const EMPTY_TOOLTIPS: MapTooltipPayload[] = [];
const EMPTY_POSITIONS: Point2D[] = [];

type GeometryMeasurementEdit = {
  key: string;
  value: string;
} & (
  | {
      kind: 'storey-height';
      storeyHeight: NonNullable<MapTooltipPayload['storeyHeight']>;
    }
  | { kind: 'segment'; segment: MapTooltipSegmentRef }
  | { kind: 'corner-angle'; cornerAngle: MapTooltipCornerAngleRef }
);

type GeometryMeasurementError = {
  key: string;
  message: string;
};

const stepGeometryMeasurementValue = (
  edit: GeometryMeasurementEdit,
  direction: 'up' | 'down',
  accelerated: boolean,
): string | undefined => {
  if (!edit.value.trim()) return undefined;
  const currentValue = Number(edit.value);
  if (!Number.isFinite(currentValue)) return undefined;

  const directionSign = direction === 'up' ? 1 : -1;
  const nextValue = accelerated
    ? currentValue + directionSign * (edit.kind === 'corner-angle' ? 5 : 10)
    : direction === 'up'
      ? Math.floor(currentValue) + 1
      : Math.ceil(currentValue) - 1;
  const maximum = edit.kind === 'corner-angle' ? 179.9 : Number.MAX_VALUE;
  const clampedValue = Math.min(
    maximum,
    Math.max(
      edit.kind === 'storey-height' ? STOREY_MIN_HEIGHT : 0.1,
      nextValue,
    ),
  );

  return String(Number(clampedValue.toFixed(10)));
};

/**
 * Stable layout key so tooltip array identity churn does not re-measure forever.
 */
const getTooltipsLayoutKey = (tooltips: readonly MapTooltipPayload[]): string =>
  tooltips
    .map(
      (tooltip) =>
        `${tooltip.type}:${tooltip.tone ?? ''}:${tooltip.text}:${tooltip.screenPoint[0]},${tooltip.screenPoint[1]}:${tooltip.segment?.screenStart.join(',') ?? ''}:${tooltip.segment?.screenEnd.join(',') ?? ''}:${getMeasurementKey(tooltip) ?? ''}`,
    )
    .join('|');

export const MapTooltipLayer = ({
  map,
  disabled,
  extraTooltips = EMPTY_EXTRA_TOOLTIPS,
}: MapTooltipLayerProps) => {
  const { classes, cx } = useStyles();
  const setSelectedStoreyId = useSetSelectedStoreyId();
  const [streamTooltips, setStreamTooltips] =
    useState<MapTooltipPayload[]>(EMPTY_TOOLTIPS);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const segmentInputRef = useRef<HTMLInputElement | null>(null);
  const segmentInputRefs = useRef(new Map<string, HTMLInputElement>());
  const cancelBlurRef = useRef(false);
  const pendingMeasurementFocusKeyRef = useRef<string | undefined>(undefined);
  const pendingShortcutMeasurementFocusKeyRef = useRef<string | undefined>(
    undefined,
  );
  const pendingMeasurementSelectionKeyRef = useRef<string | undefined>(
    undefined,
  );
  const pendingNumericStartCaretKeyRef = useRef<string | undefined>(undefined);
  const submittingSegmentRef = useRef(new Set<string>());
  const [segmentEdit, setSegmentEdit] = useState<GeometryMeasurementEdit>();
  const segmentEditRef = useRef(segmentEdit);
  segmentEditRef.current = segmentEdit;
  const [hoveredMeasurementKey, setHoveredMeasurementKey] = useState<string>();
  const [isPointDragging, setIsPointDragging] = useState(false);
  const [measurementError, setMeasurementError] =
    useState<GeometryMeasurementError>();

  const tooltips = useMemo(() => {
    if (!extraTooltips.length && !streamTooltips.length) return EMPTY_TOOLTIPS;
    return [...extraTooltips, ...streamTooltips];
  }, [extraTooltips, streamTooltips]);
  const tooltipsLayoutKey = useMemo(
    () => getTooltipsLayoutKey(tooltips),
    [tooltips],
  );
  const tooltipsRef = useRef(tooltips);
  tooltipsRef.current = tooltips;
  const extraTooltipsRef = useRef(extraTooltips);
  extraTooltipsRef.current = extraTooltips;
  const streamTooltipsRef = useRef<MapTooltipPayload[]>(EMPTY_TOOLTIPS);
  const positionsRef = useRef<Point2D[]>(EMPTY_POSITIONS);
  const placeCurrentTooltips = useCallback(() => {
    const extra = extraTooltipsRef.current;
    const stream = streamTooltipsRef.current;
    const merged =
      !extra.length && !stream.length ? EMPTY_TOOLTIPS : [...extra, ...stream];
    const nextPositions = measureMapTooltipPositions({
      tooltips: merged,
      layerElement: layerRef.current,
      tooltipElements: tooltipRefs.current,
    });
    positionsRef.current = nextPositions;
    writeTooltipPositions(tooltipRefs.current, nextPositions);
  }, []);

  useLayoutEffect(() => {
    const key = pendingMeasurementSelectionKeyRef.current;
    if (!key || segmentEdit?.key !== key) return;
    const input = segmentInputRefs.current.get(key);
    if (input && input === document.activeElement) {
      input.select();
      pendingMeasurementSelectionKeyRef.current = undefined;
    }
  }, [segmentEdit?.key, segmentEdit?.value]);

  useLayoutEffect(() => {
    const key = pendingNumericStartCaretKeyRef.current;
    if (!key || segmentEdit?.key !== key) return;
    const input = segmentInputRefs.current.get(key);
    if (input && input === document.activeElement) {
      const caret = input.value.length;
      input.setSelectionRange(caret, caret);
      pendingNumericStartCaretKeyRef.current = undefined;
    }
  }, [segmentEdit?.key, segmentEdit?.value]);

  useLayoutEffect(() => {
    const key = pendingShortcutMeasurementFocusKeyRef.current;
    if (!key) return;
    const input = segmentInputRefs.current.get(key);
    if (!input) return;
    input.focus();
    input.select();
    pendingShortcutMeasurementFocusKeyRef.current = undefined;
  }, [tooltipsLayoutKey]);

  useEffect(() => {
    if (!map || disabled) return;
    const subscription = map.state?.storeyHeightFocus$?.subscribe(
      (storeyId) => {
        const key = `storey-height:${storeyId}`;
        pendingShortcutMeasurementFocusKeyRef.current = key;
        const input = segmentInputRefs.current.get(key);
        if (input) {
          input.focus();
          input.select();
          pendingShortcutMeasurementFocusKeyRef.current = undefined;
        }
      },
    );
    return () => subscription?.unsubscribe();
  }, [map, disabled]);

  useEffect(() => {
    if (!map || disabled) {
      setIsPointDragging(false);
      streamTooltipsRef.current = EMPTY_TOOLTIPS;
      setStreamTooltips(EMPTY_TOOLTIPS);
      return;
    }
    const streams = map.getInteractionStreams();
    if (!streams) {
      setIsPointDragging(false);
      streamTooltipsRef.current = EMPTY_TOOLTIPS;
      setStreamTooltips(EMPTY_TOOLTIPS);
      return;
    }
    const subscription = new Subscription();
    subscription.add(
      streams.pointMove$
        .pipe(rxMap(Boolean), distinctUntilChanged())
        .subscribe((dragging) => {
          setIsPointDragging(dragging);
          if (dragging) setHoveredMeasurementKey(undefined);
        }),
    );
    subscription.add(
      streams.tooltip$.subscribe((nextStream) => {
        streamTooltipsRef.current = nextStream;
        // Local geometry changes can advance focus before React renders.
        tooltipsRef.current = [...extraTooltipsRef.current, ...nextStream];
        // Camera moves defer React renders, so pin the nodes before paint.
        placeCurrentTooltips();
        setStreamTooltips(nextStream);
      }),
    );
    return () => {
      subscription.unsubscribe();
      streamTooltipsRef.current = EMPTY_TOOLTIPS;
      setStreamTooltips(EMPTY_TOOLTIPS);
    };
  }, [disabled, map, placeCurrentTooltips]);

  useEffect(() => () => map?.setGeometryMeasurementFocus(undefined), [map]);

  useLayoutEffect(() => {
    placeCurrentTooltips();
    // Key content, not array identity — parent often passes a fresh `[]` / mapped list.
  }, [
    hoveredMeasurementKey,
    segmentEdit?.key,
    segmentEdit?.value,
    tooltipsLayoutKey,
    placeCurrentTooltips,
  ]);

  const beginSegmentEdit = (segment: MapTooltipSegmentRef): void => {
    const value = formatSegmentLengthMeters(segment.lengthMeters);
    if (!value) return;
    cancelBlurRef.current = false;
    setMeasurementError(undefined);
    setSegmentEdit({
      key: getSegmentKey(segment),
      kind: 'segment',
      segment,
      value,
    });
  };

  const beginCornerAngleEdit = (
    cornerAngle: MapTooltipCornerAngleRef,
  ): void => {
    const value = formatCornerAngleDegrees(cornerAngle.angleDegrees);
    if (!value) return;
    cancelBlurRef.current = false;
    setMeasurementError(undefined);
    setSegmentEdit({
      key: getCornerAngleKey(cornerAngle),
      kind: 'corner-angle',
      cornerAngle,
      value,
    });
  };

  useEffect(() => {
    if (!map || disabled) return;
    const handleGeometryKeyboardShortcut = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat)
        return;
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement instanceof HTMLSelectElement ||
        activeElement?.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        if (map.isExactFacadeMode) {
          const selectedHeight = tooltipsRef.current.find(
            (tooltip) => tooltip.selected && tooltip.storeyHeight,
          );
          const key = selectedHeight && getMeasurementKey(selectedHeight);
          if (!key) return;
          event.preventDefault();
          const input = segmentInputRefs.current.get(key);
          if (input) {
            input.focus();
            input.select();
          }
          return;
        }
        const segment = map.getLargestSelectedStoreySegment();
        if (!segment) return;
        event.preventDefault();
        const key = getSegmentKey(segment);
        pendingShortcutMeasurementFocusKeyRef.current = key;
        const input = segmentInputRefs.current.get(key);
        if (input) {
          input.focus();
          input.select();
          pendingShortcutMeasurementFocusKeyRef.current = undefined;
        } else {
          map.setGeometryMeasurementFocus({
            kind: 'segment',
            shapeId: segment.shapeId,
          });
        }
        return;
      }

      if (!/^\d$/.test(event.key)) return;
      const selectedCorner = map.getSingleSelectedCorner();
      if (!selectedCorner) return;
      const key = getCornerAngleKey({
        ...selectedCorner,
        angleDegrees: 0,
        pointsAreClockwise: false,
      });
      const tooltip = tooltipsRef.current.find(
        (candidate) => getMeasurementKey(candidate) === key,
      );
      const input = segmentInputRefs.current.get(key);
      if (!tooltip?.cornerAngle || !input) return;

      event.preventDefault();
      pendingMeasurementSelectionKeyRef.current = undefined;
      pendingNumericStartCaretKeyRef.current = key;
      input.focus();
      setSegmentEdit({
        key,
        kind: 'corner-angle',
        cornerAngle: tooltip.cornerAngle,
        value: event.key,
      });
    };
    window.addEventListener('keydown', handleGeometryKeyboardShortcut);
    return () =>
      window.removeEventListener('keydown', handleGeometryKeyboardShortcut);
  }, [disabled, map]);

  const submitSegmentEdit = async (
    edit: GeometryMeasurementEdit,
    onLocalApplied?: () => void,
  ): Promise<boolean> => {
    if (submittingSegmentRef.current.has(edit.key)) return false;
    const numericValue =
      edit.kind === 'corner-angle'
        ? parseCornerAngleInput(edit.value)
        : parseSegmentLengthInput(edit.value);
    const input = segmentInputRef.current;
    if (
      numericValue === undefined ||
      (edit.kind === 'storey-height' && numericValue < STOREY_MIN_HEIGHT)
    ) {
      const message =
        edit.kind === 'storey-height'
          ? `Height must be at least ${STOREY_MIN_HEIGHT} m`
          : edit.kind !== 'corner-angle'
            ? 'Length must be at least 0.1 m'
            : 'Angle must be between 0.1° and 179.9°';
      input?.setCustomValidity(message);
      setMeasurementError({ key: edit.key, message });
      requestAnimationFrame(() => {
        if (!segmentInputRef.current || segmentInputRef.current === input) {
          input?.focus();
        }
      });
      return false;
    }

    input?.setCustomValidity('');
    setMeasurementError(undefined);
    submittingSegmentRef.current.add(edit.key);
    let locallyApplied = false;
    const finishLocalEdit = (): void => {
      if (locallyApplied) return;
      locallyApplied = true;
      submittingSegmentRef.current.delete(edit.key);
      setSegmentEdit((current) => (current === edit ? undefined : current));
      if (onLocalApplied) {
        cancelBlurRef.current = true;
        onLocalApplied();
      }
    };
    try {
      // Focusing exposes a rounded value. Leaving it unchanged must not
      // resize the geometry or wait for a project save on every Tab.
      const originalValue =
        edit.kind === 'storey-height'
          ? formatSegmentLengthMeters(edit.storeyHeight.heightMeters)
          : edit.kind === 'segment'
            ? formatSegmentLengthMeters(edit.segment.lengthMeters)
            : formatCornerAngleDegrees(edit.cornerAngle.angleDegrees);
      const applied =
        numericValue === Number(originalValue)
          ? true
          : edit.kind === 'storey-height'
            ? await map?.resizeStoreyHeight(
                edit.storeyHeight.storeyId,
                numericValue,
                finishLocalEdit,
              )
            : edit.kind === 'segment'
              ? onLocalApplied
                ? await map?.resizeSegmentLength(
                    edit.segment,
                    numericValue,
                    finishLocalEdit,
                  )
                : await map?.resizeSegmentLength(edit.segment, numericValue)
              : onLocalApplied
                ? await map?.resizeCornerAngle(
                    edit.cornerAngle,
                    numericValue,
                    finishLocalEdit,
                  )
                : await map?.resizeCornerAngle(edit.cornerAngle, numericValue);
      if (applied) {
        finishLocalEdit();
        if (
          !onLocalApplied &&
          segmentEditRef.current === edit &&
          input &&
          input === document.activeElement
        ) {
          cancelBlurRef.current = true;
          input.blur();
        }
        return true;
      } else {
        const message =
          edit.kind === 'storey-height'
            ? 'The storey cannot accommodate this height'
            : edit.kind === 'segment'
              ? 'The neighboring segments cannot accommodate this length'
              : 'The corner cannot accommodate this angle';
        input?.setCustomValidity(message);
        setMeasurementError({ key: edit.key, message });
        requestAnimationFrame(() => {
          if (!segmentInputRef.current || segmentInputRef.current === input) {
            input?.focus();
          }
        });
        return false;
      }
    } catch {
      // Persistence already reports save failures. An older request must not
      // attach its error to a new editing session on the same DOM input.
      if (locallyApplied || segmentEditRef.current !== edit) return false;
      const message =
        edit.kind === 'storey-height'
          ? 'The storey height could not be saved'
          : edit.kind === 'segment'
            ? 'The segment length could not be saved'
            : 'The corner angle could not be saved';
      input?.setCustomValidity(message);
      setMeasurementError({ key: edit.key, message });
      requestAnimationFrame(() => {
        if (!segmentInputRef.current || segmentInputRef.current === input) {
          input?.focus();
        }
      });
      return false;
    } finally {
      if (!locallyApplied) submittingSegmentRef.current.delete(edit.key);
    }
  };

  if (!map || disabled || !tooltips.length) return null;

  return (
    <div className={classes.layer} ref={layerRef}>
      {tooltips.map((tooltip, index) => {
        const position = positionsRef.current[index];
        const left = position?.[0] ?? tooltip.screenPoint[0] + TOOLTIP_OFFSET_X;
        const top = position?.[1] ?? tooltip.screenPoint[1] + TOOLTIP_OFFSET_Y;
        const segmentKey = getMeasurementKey(tooltip);
        const activeSegmentEdit =
          segmentKey && segmentEdit?.key === segmentKey
            ? segmentEdit
            : undefined;
        const segmentLengthValue = tooltip.text.replace(/(?: m|°)$/, '');
        const hoveredSegmentLengthValue =
          !isPointDragging &&
          tooltip.segment &&
          hoveredMeasurementKey === segmentKey
            ? formatSegmentLengthMeters(tooltip.segment.lengthMeters)
            : undefined;
        const displayedSegmentLengthValue =
          activeSegmentEdit?.value ??
          hoveredSegmentLengthValue ??
          segmentLengthValue;
        const displayedInputValue = displayedSegmentLengthValue;
        const isEditableMeasurement = Boolean(
          tooltip.segment || tooltip.cornerAngle || tooltip.storeyHeight,
        );
        const measurementUnit = tooltip.cornerAngle ? '°' : 'm';
        const activeMeasurementError =
          segmentKey && measurementError?.key === segmentKey
            ? measurementError.message
            : undefined;

        return (
          <Tooltip
            key={`${tooltip.type}-${tooltip.tone ?? 'default'}-${segmentKey ?? index}`}
            title={
              tooltip.storeyHeight && !activeSegmentEdit && !isPointDragging
                ? 'Storey height'
                : undefined
            }
            describeChild
          >
            <div
              className={cx(
                classes.tooltip,
                tooltip.disabled && classes.tooltipDisabled,
                tooltip.tone === 'muted' && classes.tooltipMuted,
                tooltip.tone === 'calibrate' && classes.tooltipCalibrate,
                tooltip.tone === 'segment-length' &&
                  classes.tooltipSegmentLength,
                tooltip.selected && classes.selectedMeasurement,
              )}
              ref={(element) => {
                tooltipRefs.current[index] = element;
              }}
              data-storey-height-id={tooltip.storeyHeight?.storeyId}
              data-point-dragging={
                isEditableMeasurement && isPointDragging ? true : undefined
              }
              style={{
                left: `${left}px`,
                top: `${top}px`,
              }}
              onClick={(event) => {
                if (!isEditableMeasurement) return;
                event.stopPropagation();
                event.currentTarget.querySelector('input')?.focus();
              }}
              onPointerDown={(event) => {
                if (isEditableMeasurement) event.stopPropagation();
              }}
              onPointerEnter={() => {
                if (segmentKey && !isPointDragging) {
                  setHoveredMeasurementKey(segmentKey);
                }
              }}
              onPointerLeave={() => {
                setHoveredMeasurementKey((current) =>
                  current === segmentKey ? undefined : current,
                );
              }}
            >
              {tooltip.tone === 'segment-length' ? (
                <>
                  {tooltip.storeyHeight && (
                    <span
                      className={classes.storeyHeightIcon}
                      data-storey-height-icon
                      aria-hidden="true"
                    >
                      <Icon category="quantity" icon="height" size={12} />
                    </span>
                  )}
                  <Tooltip
                    title={
                      activeMeasurementError ? (
                        <span className={classes.validationTooltipContent}>
                          <Icon
                            category="mui"
                            icon="WarningAmber"
                            size="small"
                            color="warning"
                          />
                          <span>{activeMeasurementError}</span>
                        </span>
                      ) : undefined
                    }
                    open={Boolean(activeMeasurementError)}
                    placement="bottom"
                    tooltipClassName={classes.validationTooltip}
                    describeChild
                    arrow
                  >
                    <input
                      aria-label={
                        tooltip.storeyHeight
                          ? 'Storey height in meters'
                          : tooltip.cornerAngle
                            ? 'Corner angle in degrees'
                            : 'Segment length in meters'
                      }
                      className={cx(
                        classes.segmentLengthInput,
                        tooltip.cornerAngle && classes.cornerAngleInput,
                      )}
                      ref={(element) => {
                        if (!segmentKey) return;
                        if (element) {
                          segmentInputRefs.current.set(segmentKey, element);
                        } else {
                          segmentInputRefs.current.delete(segmentKey);
                        }
                      }}
                      inputMode="decimal"
                      type="text"
                      value={displayedInputValue}
                      onChange={(event) => {
                        event.currentTarget.setCustomValidity('');
                        setMeasurementError((current) =>
                          current?.key === segmentKey ? undefined : current,
                        );
                        if (!activeSegmentEdit) return;
                        const normalizedValue = normalizeSegmentLengthInput(
                          event.target.value,
                        );
                        if (normalizedValue === undefined) return;
                        setSegmentEdit((current) =>
                          current?.key === activeSegmentEdit.key
                            ? { ...current, value: normalizedValue }
                            : current,
                        );
                      }}
                      onFocus={(event) => {
                        segmentInputRef.current = event.currentTarget;
                        pendingMeasurementFocusKeyRef.current = undefined;
                        pendingShortcutMeasurementFocusKeyRef.current =
                          undefined;
                        const isNumericStartFocus =
                          segmentKey !== undefined &&
                          pendingNumericStartCaretKeyRef.current === segmentKey;
                        if (segmentKey && !isNumericStartFocus) {
                          pendingMeasurementSelectionKeyRef.current =
                            segmentKey;
                        }
                        const focusedTooltip =
                          tooltipsRef.current.find(
                            (candidate) =>
                              getMeasurementKey(candidate) === segmentKey,
                          ) ?? tooltip;
                        const focusedMeasurement =
                          focusedTooltip.segment ?? focusedTooltip.cornerAngle;
                        if (focusedMeasurement) {
                          map.setGeometryMeasurementFocus({
                            kind: tooltip.cornerAngle
                              ? 'corner-angle'
                              : 'segment',
                            shapeId: focusedMeasurement.shapeId,
                          });
                        }
                        if (focusedTooltip.storeyHeight && !activeSegmentEdit) {
                          cancelBlurRef.current = false;
                          setMeasurementError(undefined);
                          setSegmentEdit({
                            key: getMeasurementKey(focusedTooltip) ?? '',
                            kind: 'storey-height',
                            storeyHeight: focusedTooltip.storeyHeight,
                            value:
                              formatSegmentLengthMeters(
                                focusedTooltip.storeyHeight.heightMeters,
                              ) ?? '',
                          });
                        } else if (
                          focusedTooltip.segment &&
                          !activeSegmentEdit
                        ) {
                          beginSegmentEdit(focusedTooltip.segment);
                        } else if (
                          focusedTooltip.cornerAngle &&
                          !activeSegmentEdit
                        ) {
                          beginCornerAngleEdit(focusedTooltip.cornerAngle);
                        }
                        if (!isNumericStartFocus) {
                          event.currentTarget.select();
                        }
                      }}
                      onKeyDown={(event) => {
                        if (!activeSegmentEdit) return;
                        if (
                          event.key === 'ArrowUp' ||
                          event.key === 'ArrowDown'
                        ) {
                          event.preventDefault();
                          const nextValue = stepGeometryMeasurementValue(
                            activeSegmentEdit,
                            event.key === 'ArrowUp' ? 'up' : 'down',
                            event.shiftKey,
                          );
                          if (nextValue === undefined) return;
                          event.currentTarget.setCustomValidity('');
                          setMeasurementError((current) =>
                            current?.key === segmentKey ? undefined : current,
                          );
                          setSegmentEdit((current) =>
                            current?.key === activeSegmentEdit.key
                              ? { ...current, value: nextValue }
                              : current,
                          );
                        } else if (
                          event.key === 'Tab' &&
                          activeSegmentEdit.kind === 'storey-height'
                        ) {
                          const adjacentId = event.shiftKey
                            ? tooltip.storeyHeight?.previousStoreyId
                            : tooltip.storeyHeight?.nextStoreyId;
                          if (!adjacentId) return;
                          event.preventDefault();
                          const adjacentKey = `storey-height:${adjacentId}`;
                          void submitSegmentEdit(activeSegmentEdit, () => {
                            if (
                              adjacentId ===
                              activeSegmentEdit.storeyHeight.storeyId
                            ) {
                              cancelBlurRef.current = false;
                              segmentInputRefs.current
                                .get(adjacentKey)
                                ?.select();
                              setSegmentEdit({
                                ...activeSegmentEdit,
                                storeyHeight: {
                                  ...activeSegmentEdit.storeyHeight,
                                  heightMeters: Number(activeSegmentEdit.value),
                                },
                              });
                              return;
                            }
                            pendingShortcutMeasurementFocusKeyRef.current =
                              adjacentKey;
                            setSelectedStoreyId(adjacentId);
                            map.state.hoveredStoreyId$.next(adjacentId);
                            const input =
                              segmentInputRefs.current.get(adjacentKey);
                            if (input) {
                              input.focus();
                              input.select();
                              pendingShortcutMeasurementFocusKeyRef.current =
                                undefined;
                            }
                          });
                        } else if (
                          event.key === 'Tab' &&
                          activeSegmentEdit.kind !== 'storey-height'
                        ) {
                          const adjacentKey = getAdjacentMeasurementKey(
                            tooltips,
                            activeSegmentEdit.key,
                            activeSegmentEdit.kind === 'segment'
                              ? activeSegmentEdit.segment.shapeId
                              : activeSegmentEdit.cornerAngle.shapeId,
                            activeSegmentEdit.kind === 'segment'
                              ? activeSegmentEdit.segment.pointsAreClockwise
                              : activeSegmentEdit.cornerAngle
                                  .pointsAreClockwise,
                            event.shiftKey,
                          );
                          if (!adjacentKey) return;
                          event.preventDefault();
                          pendingMeasurementFocusKeyRef.current = adjacentKey;
                          void submitSegmentEdit(activeSegmentEdit, () => {
                            const adjacentInput =
                              segmentInputRefs.current.get(adjacentKey);
                            if (adjacentInput) {
                              adjacentInput.focus();
                              adjacentInput.select();
                            } else {
                              pendingMeasurementFocusKeyRef.current = undefined;
                              map.setGeometryMeasurementFocus(undefined);
                            }
                          }).then((submitted) => {
                            if (!submitted)
                              pendingMeasurementFocusKeyRef.current = undefined;
                          });
                        } else if (event.key === 'Enter' && event.shiftKey) {
                          event.preventDefault();
                          cancelBlurRef.current = true;
                          event.currentTarget.setCustomValidity('');
                          setMeasurementError(undefined);
                          setSegmentEdit(undefined);
                          event.currentTarget.blur();
                        } else if (event.key === 'Enter') {
                          event.preventDefault();
                          const input = event.currentTarget;
                          void submitSegmentEdit(activeSegmentEdit, () => {
                            if (
                              segmentEditRef.current !== activeSegmentEdit ||
                              document.activeElement !== input
                            ) {
                              cancelBlurRef.current = false;
                              return;
                            }
                            input.blur();
                          });
                        } else if (event.key === 'Escape') {
                          event.preventDefault();
                          cancelBlurRef.current = true;
                          event.currentTarget.setCustomValidity('');
                          setMeasurementError(undefined);
                          setSegmentEdit(undefined);
                          event.currentTarget.blur();
                        }
                      }}
                      onBlur={() => {
                        if (!pendingMeasurementFocusKeyRef.current) {
                          map.setGeometryMeasurementFocus(undefined);
                        }
                        if (cancelBlurRef.current) {
                          cancelBlurRef.current = false;
                          segmentInputRef.current = null;
                          return;
                        }
                        if (activeSegmentEdit) {
                          void submitSegmentEdit(activeSegmentEdit);
                        }
                        segmentInputRef.current = null;
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        if (
                          event.button !== 0 ||
                          document.activeElement === event.currentTarget
                        ) {
                          return;
                        }
                        // Own the initial focus gesture so a few pixels of pointer
                        // movement cannot replace select-all with a partial range.
                        event.preventDefault();
                        event.currentTarget.focus();
                        event.currentTarget.select();
                      }}
                    />
                  </Tooltip>
                  {tooltip.cornerAngle ? (
                    <span className={classes.cornerAngleSymbol}>°</span>
                  ) : (
                    <>
                      <span
                        aria-hidden="true"
                        className={classes.segmentLengthSizer}
                        data-segment-length-sizer
                      >
                        {formatSegmentLengthMeters(
                          tooltip.segment?.lengthMeters ??
                            tooltip.storeyHeight?.heightMeters ??
                            Number.NaN,
                        )}
                      </span>{' '}
                      <span className={classes.segmentLengthUnit}>
                        {measurementUnit}
                      </span>
                    </>
                  )}
                </>
              ) : (
                tooltip.text
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};
